import { Address, Value } from "./value.js";
import { VersionedEntry } from "./store.js";

export type RuntimeIterator<Yield = never> = {
  next(): Generator<Yield, Value, void>;
};

const some = (value: Value): Value => ({ kind: "enum", enumName: "Option", variant: "Some", fields: { value } });
const none: Value = { kind: "enum", enumName: "Option", variant: "None", fields: {} };

/** The numeric index a List child address encodes (`…/n<index>` — see `keySegment`
 *  for an int key). Used to walk a List in ascending index order. */
function listIndexOf(childAddr: Address): number {
  const seg = childAddr.slice(childAddr.lastIndexOf("/") + 1);
  return seg.startsWith("n") ? Number(seg.slice(1)) : 0;
}

/**
 * Lazy iterator over a built-in collection (Set, Dict, List, Table).
 *
 * The keyset address and child address list are snapshotted via `readEntry` and
 * `liveChildren` at construction time, establishing the MVCC read footprint.
 * Individual entries are then read on demand as the loop advances.
 */
export class CollectionIterator implements RuntimeIterator<never> {
  private children: Address[];
  private i = 0;
  private readonly yieldValue: (e: { key: Value; value: Value }) => Value;

  constructor(
    collAddr: Address,
    container: string,
    readEntry: (addr: Address) => VersionedEntry | undefined,
    liveChildren: (addr: Address) => Address[],
  ) {
    // Read keyset now so a concurrent structural change (insert/delete) conflicts.
    readEntry(`${collAddr}#keys`);
    this.children = liveChildren(collAddr);
    // A List is ORDERED: iterate by ascending index. `liveChildren` returns entries
    // in store-insertion order, which for append-only children is already index
    // order, but a buffered-then-committed mix can reorder them — so sort by the
    // numeric index encoded in the child address (`…/n<index>`) to be exact.
    if (container === "List") {
      this.children.sort((a, b) => listIndexOf(a) - listIndexOf(b));
    }
    this.readEntry = readEntry;
    // A Dict is an iterable of `Pair<K, V>`: yield each entry as an inline `Pair`
    // record (first = key, second = value). It's a value-typed snapshot, so
    // iterating never allocates a store object or writes during a read. A List
    // yields the entry's value; a Set its key (membership).
    this.yieldValue =
      container === "Dict"
        ? (e) => ({ kind: "record", typeName: "Pair", fields: { first: e.key, second: e.value } })
        : container === "List"
          ? (e) => e.value
          : (e) => e.key;
  }

  private readonly readEntry: (addr: Address) => VersionedEntry | undefined;

  *next(): Generator<never, Value, void> {
    while (this.i < this.children.length) {
      const entry = this.readEntry(this.children[this.i++]!);
      if (entry?.value?.kind === "entry") {
        return some(this.yieldValue(entry.value as { key: Value; value: Value }));
      }
    }
    return none;
  }
}
